// The "Back to Shopify" link, and the only door it is allowed to open.
//
// `r` arrives in the capture link as a HINT, like `m`, `s` and `p` — anyone who
// can edit a URL can edit it. Rendered unchecked it is an open redirect: a link
// that says "photograph this product" and sends the phone to a lookalike login
// page afterwards. So the only URL this returns is this store's own admin,
// under its apps path, over https, on the real host and nothing else.
//
// The shop it is checked against is the SESSION's, read from the grant — never
// `s`, which is as editable as `r` itself.

const ADMIN_HOST = "admin.shopify.com";

/**
 * @param {unknown} raw         the `r` hint, or a value read back from storage
 * @param {unknown} shopDomain  the session's shop, e.g. "variety.myshopify.com"
 * @returns {string|null}       the normalised URL, or null if it is not ours
 */
export function safeReturnUrl(raw, shopDomain) {
  if (typeof raw !== "string" || !raw) return null;
  if (typeof shopDomain !== "string") return null;
  const handle = shopDomain.replace(/\.myshopify\.com$/i, "").toLowerCase();
  if (!handle) return null;

  let u;
  try {
    u = new URL(raw);
  } catch {
    return null;
  }
  // `hostname` alone would accept a port; `host` alone would miss nothing but
  // reads less plainly. Credentials are refused too: nothing we mint has them.
  if (u.protocol !== "https:") return null;
  if (u.hostname !== ADMIN_HOST || u.port !== "") return null;
  if (u.username || u.password) return null;

  // Checked AFTER parsing, so `..` and `%2e%2e` are already resolved and
  // cannot walk out of /store/<handle>/.
  const [, store, pathHandle, apps] = u.pathname.split("/");
  if (store !== "store" || apps !== "apps") return null;
  if (!pathHandle || pathHandle.toLowerCase() !== handle) return null;

  return u.href;
}

/**
 * Adds `qs=<grantId>` to an already-validated return URL: the admin app reads
 * it as "the phone has filed", checks it against its saved link and asks the
 * server once. The path stays inside the allowed admin app path; a bad input
 * returns the URL unchanged.
 * @param {string|null|undefined} url  a value safeReturnUrl returned
 * @param {unknown} grantId
 */
export function withQuickHint(url, grantId) {
  if (typeof url !== "string" || !url || typeof grantId !== "string" || !grantId) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("qs", grantId);
    return u.href;
  } catch {
    return url;
  }
}

/**
 * The one place the Done screen's way back to Shopify is decided, so the
 * automatic return, the iOS scheme and the "Back to Shopify" tap all use the
 * same URL (a preset's own, else Quick scan's with its ?qs grant hint).
 * @param {{returnUrl?: string|null}|null|undefined} preset
 * @param {{returnUrl?: string|null, grantId?: string|null}|null|undefined} quick
 */
export function doneReturnUrl(preset, quick) {
  return preset?.returnUrl || withQuickHint(quick?.returnUrl, quick?.grantId) || "";
}

/**
 * `rp` is the page the merchant tapped from (a filtered Products view, say),
 * appended to the capture URL at tap time so ONE pre-minted link serves every
 * page. It is as editable as `r`, so it is a hint: it is used only if it is a
 * same-origin relative `/app...` path, and then only to replace the path under
 * the `r` URL's own `/store/<handle>/apps/<key>` prefix. Anything else (a
 * scheme, `//`, a backslash, control characters, `..`, an encoded dot or slash
 * in the path) and `r` is returned untouched, i.e. the path the link was
 * minted with. The result is still checked by safeReturnUrl afterwards.
 * @param {unknown} r   the link's `r` hint
 * @param {unknown} rp  the link's `rp` hint
 * @returns {unknown}   `r` with its app path swapped for `rp`, or `r`
 */
export function applyReturnPath(r, rp) {
  if (typeof r !== "string" || typeof rp !== "string" || !rp || rp.length > 2000) return r;
  if (!/^\/app(\/|\?|$)/.test(rp)) return r;
  if (/[\\\u0000- \u007f]/.test(rp) || rp.includes("//")) return r;
  const path = rp.split(/[?#]/)[0];
  if (/(^|\/)\.\.?(\/|$)/.test(path) || /%2e|%2f|%5c|%00/i.test(path)) return r;
  let u;
  try {
    u = new URL(r);
  } catch {
    return r;
  }
  const seg = u.pathname.split("/"); // "", store, <handle>, apps, <key>, ...app path
  if (seg[1] !== "store" || seg[3] !== "apps" || !seg[4]) return r;
  const base = seg.slice(0, 5).join("/");
  let next;
  try {
    next = new URL(base + rp, u.origin);
  } catch {
    return r;
  }
  if (next.origin !== u.origin || !next.pathname.startsWith(base + "/app")) return r;
  return next.href;
}
